import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { authenticationQueryKeyMap } from '@/features/authentication/_utils/query-keys';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';

import { queryKeyMap as projectQueryKeyMap } from '@/features/project/_utils/query-keys';
import {
  type BulkUpsertAndDeleteStagesSchema,
  CreateWorkflowParamsSchema,
  UpdateWorkflowParamsSchema,
  WorkflowListModel,
  type WorkflowListParamsSchema,
  WorkflowModel,
} from '../models/workflow';
import { queryKeyMap } from '../utils/query-keys';

const API_BASE_URL = '/api/workflows/';

export const useWorkflowList = (
  params?: z.infer<typeof WorkflowListParamsSchema>,
  enabled = true,
) => {
  const payload = params
    ? {
        ...params,
        tagIds: params.tagIds?.at(0) === 'null' ? [] : params.tagIds,
        isActionRequired:
          !params.actionRequired || params.actionRequired === 'all'
            ? undefined
            : params.actionRequired === 'action-required',
      }
    : undefined;

  return useQuery({
    queryKey: queryKeyMap.workflowList(payload),
    queryFn: async () => {
      const res = await axiosGet(API_BASE_URL, payload);

      return WorkflowListModel.parse(res);
    },
    enabled,
  });
};

export const useWorkflow = (id: string) => {
  return useQuery({
    queryKey: queryKeyMap.workflow(id),
    queryFn: async () => {
      const workflow = await axiosGet(`${API_BASE_URL}${id}/`);

      return WorkflowModel.parse(workflow);
    },
    enabled: !!id,
  });
};

export const useCreateWorkflow = ({
  projectId,
}: { projectId?: string | undefined }) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof CreateWorkflowParamsSchema>) => {
      const { tagIds, deadline, ...payload } =
        CreateWorkflowParamsSchema.parse(values);
      return await axiosPost(API_BASE_URL, {
        ...payload,
        deadline: deadline?.format('YYYY-MM-DD'),
        tagIds,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(queryKeyMap.workflowList()),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
        projectId &&
          invalidate(projectQueryKeyMap.projectNotification(projectId)),
      ]);
    },
  });
};

export const useUpdateWorkflow = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: Partial<z.infer<typeof UpdateWorkflowParamsSchema>> & {
        id: string;
      },
    ) => {
      const { id, tagIds, deadline, ...payload } =
        UpdateWorkflowParamsSchema.partial().parse(values);
      return await axiosPatch(`${API_BASE_URL}${id}/`, {
        ...payload,
        deadline: deadline?.format('YYYY-MM-DD'),
        tagIds,
      });
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        invalidate(queryKeyMap.workflow(id)),
        invalidate(queryKeyMap.workflowList()),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
      ]);
    },
  });
};

export const useDeleteWorkflow = ({
  projectId,
}: { projectId?: string | undefined }) => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await axiosDelete(`${API_BASE_URL}${id}/`);
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(queryKeyMap.workflowList()),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
        projectId &&
          invalidate(projectQueryKeyMap.projectNotification(projectId)),
      ]);
    },
  });
};

export const useStartWorkflow = ({
  projectId,
}: { projectId?: string | undefined }) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosPost(`${API_BASE_URL}${id}/start/`, {});
    },
    onSuccess: async (_, id) => {
      await Promise.all([
        invalidate(queryKeyMap.workflowList()),
        invalidate(queryKeyMap.workflow(id)),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
        projectId &&
          invalidate(projectQueryKeyMap.projectNotification(projectId)),
      ]);
    },
  });
};

export const useBulkUpsertAndDeleteStages = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      values: z.infer<typeof BulkUpsertAndDeleteStagesSchema>,
    ) => {
      const { id, ...payload } = values;
      return await axiosPost(
        `${API_BASE_URL}${id}/bulk-upsert-and-delete-stages/`,
        payload,
      );
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        invalidate(queryKeyMap.workflow(id)),
        invalidate(queryKeyMap.workflowList()),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
      ]);
    },
  });
};
